<template>
  <!-- As abas do design da coleção (`TabbedSection`), sem ir à URL: a janela é por cima da tela -->
  <ontos-tabbed-section ref="tabs" :tabs="views" opens="property">
    <template #default="{tab}">
      <!-- Todas montadas, e só a aberta à vista: o salvar confere as três, e não só a que se vê -->
      <div v-show="tab === 'property'"><slot name="property"/></div>
      <div v-if="validation" v-show="tab === 'validation'"><slot name="validation"/></div>
      <div v-show="tab === 'access'"><slot name="access"/></div>
    </template>
  </ontos-tabbed-section>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import TabbedSection from '@/components/shared/TabbedSection.vue'
import {ViewTab} from '@/components/shared/ViewTabs.vue'
import {MessageKey} from '@/i18n'

export type PropertyTab = 'property' | 'validation' | 'access'
export const PROPERTY_TABS: PropertyTab[] = ['property', 'validation', 'access']

/** O rótulo e o ícone de cada aba; o de Acessos é o da aba de acesso do design da coleção. */
const TABS: Record<PropertyTab, { label: MessageKey, icon: string }> = {
  property: {label: 'property-tab.property', icon: 'mdi-form-textbox'},
  validation: {label: 'property-tab.validation', icon: 'mdi-check-circle-outline'},
  access: {label: 'property-tab.access', icon: 'mdi-shield-account'}
}

/**
 * As abas da janela de propriedade (pedido do usuário, no padrão do design da coleção): **Propriedade** —
 * a identidade, o tipo, o comportamento e o cálculo —, **Validação** — as opções permitidas e as regras —
 * e **Acessos** — "Visível quando" e "Editável quando". Um componente para o atributo e o relacionamento,
 * que põem o conteúdo de cada aba nos slots.
 *
 * Sem Validação onde não há o que validar (`validation`): o calculado e a mídia não têm regras nem
 * opções permitidas (decisão do usuário: a aba some, em vez de aparecer desabilitada).
 *
 * A aba com o que não confere ganha o ponto vermelho (`invalid`), e o salvar abre a primeira delas
 * (`reveal`) — decisão do usuário: o erro numa aba fora da vista não pode passar calado.
 */
@Component({name: 'ontos-property-tabs', components: {'ontos-tabbed-section': TabbedSection}})
class PropertyTabs extends Vue {
  @Prop({type: Boolean, default: true}) readonly validation!: boolean
  @Prop({type: Array, default: () => []}) readonly invalid!: PropertyTab[]

  get views(): ViewTab[] {
    return PROPERTY_TABS.filter(tab => tab !== 'validation' || this.validation)
        .map(value => ({value, icon: TABS[value].icon, title: this.$tt(TABS[value].label) as string,
          alert: this.invalid.includes(value)}))
  }

  /** Abre a primeira aba com o que não confere — a menos que a aberta já seja uma delas. */
  reveal(invalid: PropertyTab[]) {
    const tabs = this.$refs.tabs as { current: string, pick(tab: string): void } | undefined
    if (!tabs || !invalid.length || invalid.includes(tabs.current as PropertyTab)) return
    tabs.pick(invalid[0])
  }
}

export default toNative(PropertyTabs)
</script>
